import { redirect } from 'react-router-dom';
import { MyRouteObject, checkAdminAuth, checkTrainingAuth, checkSuperAdminAuth } from './index';
import { lazy } from 'react';

// 懒加载管理端组件
const AdminLayout = lazy(() => import('@/layouts/AdminLayout'));
const AdminDashboard = lazy(() => import('@/pages/admin/Dashboard'));
const ProblemEdit = lazy(() => import('@/pages/admin/problem/Edit'));
const ProblemDetail = lazy(() => import('@/pages/admin/problem/Detail'));
const ProblemList = lazy(() => import('@/pages/admin/problem/List'));
const TagManage = lazy(() => import('@/pages/admin/problem/TagManage'));
const TrainingList = lazy(() => import('@/pages/admin/problem/TrainingList'));
const TrainingDetail = lazy(() => import('@/pages/admin/problem/TrainingDetail'));
const TrainingEdit = lazy(() => import('@/pages/admin/problem/TrainingEdit'));
const TrainingStageList = lazy(() => import('@/pages/admin/problem/TrainingStageList'));
const SubmissionList = lazy(() => import('@/pages/admin/submission/List'));
const SubmissionDetail = lazy(() => import('@/pages/share/submission/Detail'));
const ContestList = lazy(() => import('@/pages/admin/contest/List'));
const ContestCreate = lazy(() => import('@/pages/admin/contest/Create'));
const ContestEdit = lazy(() => import('@/pages/admin/contest/Edit'));
const ContestDetail = lazy(() => import('@/pages/admin/contest/Detail'));
const ContestAnnouncements = lazy(() => import('@/pages/admin/contest/Announcements'));
const ContestProblems = lazy(() => import('@/pages/admin/contest/Problems'));
const ContestReview = lazy(() => import('@/pages/admin/contest/Review'));
// 试炼筹备 + 论功台（原顶级菜单「试炼管理」已并入比赛管理）
const ContestPrepare = lazy(() => import('@/pages/admin/contest/Prepare'));
const ContestSettlement = lazy(() => import('@/pages/admin/contest/Settlement'));
const ContestSettlementDetail = lazy(() => import('@/pages/admin/contest/SettlementDetail'));
// 站点级配置（原「系统管理 → 系统设置」的两个 Tab，现拆成两个独立页面）
const WebsiteConfigPage = lazy(() => import('@/pages/admin/setting/WebsiteConfigPage'));
const JudgeServerPage = lazy(() => import('@/pages/admin/setting/JudgeServerPage'));
const UserManagement = lazy(() => import('@/pages/admin/user'));
const TeamManagement = lazy(() => import('@/pages/admin/user/TeamManagement'));
const InvitationCodeManagement = lazy(() => import('@/pages/admin/user/components/InvitationCodeManagement'));
const GroupManagement = lazy(() => import('@/pages/admin/user/GroupManagement'));
const GroupMemberManagement = lazy(() => import('@/pages/admin/user/GroupMemberManagement'));
// 问卷系统页面
// 题库管理已改为「双栏工作台」：左栏题库类别 + 右栏题目卡片/表格（取代旧的题库列表/题库详情两级页面）
const QuestionBankWorkbench = lazy(() => import('@/pages/admin/question/QuestionBankWorkbench'));
const PaperListQuestion = lazy(() => import('@/pages/admin/question/PaperList'));
const QuestionStatistics = lazy(() => import('@/pages/admin/question/QuestionStatistics'));
const ArticleList = lazy(() => import('@/pages/admin/content/ArticleList'));
// 游戏管理
const GameList = lazy(() => import('@/pages/admin/game/GameList'));
const GameForm = lazy(() => import('@/pages/admin/game/GameForm'));
// 视频管理
const VideoList = lazy(() => import('@/pages/admin/video/VideoList'));
const VideoForm = lazy(() => import('@/pages/admin/video/VideoForm'));
// 通知管理
const NotificationManage = lazy(() => import('@/pages/admin/notification/NoticeManage'));
// 商城管理（通用商品：宠物/头像边框/AC特效/主题）
const MallList = lazy(() => import('@/pages/admin/mall/MallList'));
const MallItemForm = lazy(() => import('@/pages/admin/mall/MallItemForm'));
const DiscussionAdmin = lazy(() => import('@/pages/admin/discussion'));

// 管理端路由配置
export const adminRoutes: MyRouteObject[] = [
  {
    path: '/admin',
    element: <AdminLayout />,
    breadcrumb: '管理员',
    // 全局权限：管理员直通；「带教学班的老师」只放行训练管理（见下方 training 分支）
    loader: async () => {
      if (checkAdminAuth()) return null;
      if (await checkTrainingAuth()) return null;
      return redirect('/login');
    },
    children: [
      {
        index: true,
        element: <AdminDashboard />,
        breadcrumb: '管理控制台',
      },
      // 站点级配置：原「系统管理 → 系统设置」的两个 Tab，现拆为
      // 「网站配置」「评测服务器」两个独立菜单（左侧菜单挂在管理控制台下，仅超管可见）
      {
        path: 'setting',
        breadcrumb: '系统配置',
        loader: () => (checkSuperAdminAuth() ? null : redirect('/admin')),
        children: [
          {
            index: true,
            // 兼容旧书签 /admin/setting → 网站配置
            loader: () => redirect('/admin/setting/website'),
          },
          {
            path: 'website',
            element: <WebsiteConfigPage />,
            breadcrumb: '网站配置',
          },
          {
            path: 'judge-server',
            element: <JudgeServerPage />,
            breadcrumb: '评测服务器',
          },
        ],
      },
      {
        path: 'problem',
        breadcrumb: '题目管理',
        children: [
          {
            index: true,
            loader: () => redirect('/admin/problem/list'),
          },
          {
            path: 'list',
            element: <ProblemList />,
            breadcrumb: '题目列表',
          },
          {
            path: 'create',
            element: <ProblemEdit />,
            breadcrumb: '创建题目',
          },
          {
            path: 'edit/:id',
            element: <ProblemEdit />,
            breadcrumb: '编辑题目',
          },
          {
            path: ':id',
            element: <ProblemDetail />,
            breadcrumb: '题目详情',
          },
          {
            path: 'tags',
            element: <TagManage />,
            breadcrumb: '标签管理',
          },
          {
            // 讨论均挂在题目下，故并入题目管理
            path: 'discussion',
            element: <DiscussionAdmin />,
            breadcrumb: '讨论板管理',
          },
        ],
      },
      // 训练管理路由（管理员 + 带教学班的老师；其余管理页仍限管理员）
      {
        path: 'training',
        breadcrumb: '训练管理',
        loader: async () => ((await checkTrainingAuth()) ? null : redirect('/training')),
        children: [
          {
            index: true,
            // 管理端包装组件（pages/admin/problem/TrainingList）已内置 isAdmin/basePath，这里不再重复传
            element: <TrainingList />,
            breadcrumb: '训练列表',
          },
          {
            path: 'create',
            element: <TrainingEdit />,
            breadcrumb: '创建训练',
          },
          {
            path: 'edit/:id',
            element: <TrainingEdit />,
            breadcrumb: '编辑训练',
          },
          {
            // 阶段管理（注意：必须声明在动态段 :id 之前，语义更清晰）
            path: 'stages',
            element: <TrainingStageList />,
            breadcrumb: '阶段管理',
          },
          {
            path: ':id',
            element: <TrainingDetail />,
            breadcrumb: '训练详情',
          },
        ],
      },
      // 比赛管理路由
      {
        path: 'contest',
        breadcrumb: '比赛管理',
        children: [
          {
            index: true,
            loader: () => redirect('/admin/contest/list'),
          },
          {
            path: 'list',
            element: <ContestList />,
            breadcrumb: '比赛列表',
          },
          {
            path: 'create',
            element: <ContestCreate />,
            breadcrumb: '创建比赛',
          },
          {
            // 试炼筹备：原「试炼管理」页的筹备 tab 迁入（静态段必须排在动态段 :id 之前）
            path: 'prepare',
            element: <ContestPrepare />,
            breadcrumb: '试炼筹备',
          },
          {
            // 论功台：已结束比赛的结算入口（静态段必须排在动态段 :id 之前）
            path: 'settlement',
            element: <ContestSettlement />,
            breadcrumb: '论功台',
          },
          {
            path: 'edit/:id',
            element: <ContestEdit />,
            breadcrumb: '编辑比赛',
          },
          {
            // 单场结算：注册在 :id 之后，react-router 按具体度优先匹配不会串到比赛详情
            path: ':id/settlement',
            element: <ContestSettlementDetail />,
            breadcrumb: '比赛结算',
          },
          {
            path: ':id',
            element: <ContestDetail />,
            breadcrumb: '比赛详情',
          },
          {
            path: ':id/announcements',
            element: <ContestAnnouncements />,
            breadcrumb: '比赛公告',
          },
          {
            path: ':id/problems',
            element: <ContestProblems />,
            breadcrumb: '题目管理',
          },
          {
            path: ':id/review',
            element: <ContestReview />,
            breadcrumb: '比赛批阅',
          },
        ],
      },
      
      // 其他管理路由暂时注释掉，等组件实现后再打开
      {
        path: 'question',
        breadcrumb: '问卷管理',
        children: [
          {
            index: true,
            loader: () => redirect('/admin/question/banks'),
          },
          {
            path: 'banks',
            element: <QuestionBankWorkbench />,
            breadcrumb: '题库管理',
          },
          {
            // 兼容旧书签/外部链接：/admin/question/bank/:id → 工作台 ?bank=<id>
            path: 'bank/:id',
            loader: ({ params }) =>
              redirect(
                `/admin/question/banks${params.id ? `?bank=${params.id}` : ''}`
              ),
          },
          {
            path: 'papers',
            element: <PaperListQuestion />,
            breadcrumb: '试卷列表',
          },
          {
            path: 'statistics',
            element: <QuestionStatistics />,
            breadcrumb: '题目统计',
          },
        ],
      },
      // 用户管理路由
      {
        path: 'submission',
        breadcrumb: '提交管理',
        children: [
          {
            index: true,
            element: <SubmissionList />,
            breadcrumb: '提交列表',
            loader: () => checkAdminAuth() ? null : redirect('/login'),
          },
          {
            path: ':id',
            element: <SubmissionDetail />,
            breadcrumb: '提交详情',
            loader: () => checkAdminAuth() ? null : redirect('/login'),
          },
        ],
      },
      {
        path: 'user',
        breadcrumb: '用户管理',
        children: [
          {
            index: true,
            loader: () => redirect('/admin/user/list'),
          },
          {
            path: 'list',
            element: <UserManagement />,
            breadcrumb: '用户管理',
          },
          {
            path: 'invitation-codes',
            element: <InvitationCodeManagement />,
            breadcrumb: '邀请码管理',
          },
          {
            path: 'groups',
            element: <GroupManagement />,
            breadcrumb: '分组管理',
          },
          {
            // 分组详情管理：分组信息编辑/删除 + 组员批量管理
            path: 'groups/:id',
            element: <GroupMemberManagement />,
            breadcrumb: '分组详情',
          },
          {
            path: 'teams',
            element: <TeamManagement />,
            breadcrumb: '团队管理',
          }
        ]
      },
      {
        path: 'paper',
        breadcrumb: '试卷管理（重定向到新问卷系统）',
        children: [
          {
            index: true,
            loader: () => redirect('/admin/question/papers'),  // 重定向到新问卷系统
          },
          {
            path: 'list',
            loader: () => redirect('/admin/question/papers'),
          },
          {
            path: 'create',
            loader: () => redirect('/admin/question/paper/create'),
          },
          {
            path: 'edit/:id',
            loader: () => redirect('/admin/question/paper/edit/:id'),
          },
        ],
      },
      // 内容管理
      {
        path: 'content',
        breadcrumb: '内容管理',
        children: [
          {
            path: 'articles',
            element: <ArticleList />,
            breadcrumb: '文章管理',
          },
        ],
      },
      // 游戏管理
      {
        path: 'game',
        breadcrumb: '游戏管理',
        children: [
          {
            index: true,
            element: <GameList />,
            breadcrumb: '游戏列表',
          },
          // 上传链路已下线（docs/27）：不再有 /admin/game/create，只保留元数据编辑
          {
            path: ':gameId/edit',
            element: <GameForm />,
            breadcrumb: '编辑游戏',
          },
        ],
      },
      // 视频管理
      {
        path: 'video',
        breadcrumb: '视频管理',
        children: [
          {
            index: true,
            element: <VideoList />,
            breadcrumb: '视频列表',
          },
          {
            path: 'create',
            element: <VideoForm />,
            breadcrumb: '上传视频',
          },
          {
            path: ':videoId/edit',
            element: <VideoForm />,
            breadcrumb: '编辑视频',
          },
        ],
      },
      // 通知管理
      {
        path: 'notification',
        breadcrumb: '通知管理',
        children: [
          {
            index: true,
            loader: () => redirect('/admin/notification/notices'),
          },
          {
            path: 'notices',
            element: <NotificationManage />,
            breadcrumb: '通知管理',
          },
        ],
      },
      // 商城管理（通用商品：宠物/头像边框/AC特效/主题）
      {
        path: 'mall',
        breadcrumb: '商城管理',
        children: [
          {
            index: true,
            element: <MallList />,
            breadcrumb: '商品列表',
          },
          {
            path: 'create',
            element: <MallItemForm />,
            breadcrumb: '新增商品',
          },
          {
            path: ':key/edit',
            element: <MallItemForm />,
            breadcrumb: '编辑商品',
          },
        ],
      },
      // 旧「试炼管理」顶级菜单已下线（试炼筹备并入比赛管理、晋升改由论功台结算完成）。
      // 保留一条重定向，避免老书签与外部链接 404。
      {
        path: 'certification',
        loader: () => redirect('/admin/contest/prepare'),
      },
      // 讨论板管理已并入题目管理（旧地址 /admin/discussion 保留重定向兼容）
      {
        path: 'discussion',
        loader: () => redirect('/admin/problem/discussion'),
      },
    ],
  },
];